<template>
    <BaseModal
        :title="$t('title')"
        class="asset-sharing-modal"
        v-on="$listeners"
    >
        <BaseCard v-if="!tokenLink" padded class="l-stack l-gap-3">
            <p>{{ $t('description') }}</p>

            <div class="l-stack l-gap-2">
                <BaseInput
                    v-model="description"
                    :placeholder="$t('placeholderDescription')"
                />

                <BaseMultiselect
                    v-model="time"
                    :allow-empty="false"
                    :options="timeOptions"
                    :custom-label="
                        n =>
                            $te(n)
                                ? $t(n)
                                : n < 24 * 7
                                ? $tc('hour', n)
                                : $tc('week', n / 24 / 7)
                    "
                    :placeholder="$t('placeholderTime')"
                />

                <DateInput
                    v-if="time === 'custom'"
                    v-model="timeCustom"
                    :placeholder="$t('endDate')"
                    :disabled-dates="timeCustomDisabled"
                />
            </div>

            <div class="l-inline l-spread l-center-v">
                <span class="clickable" @click="handleClear">
                    {{ $t('clear') }}
                </span>

                <BaseButton
                    :disabled="isDisabled"
                    :is-loading="isLoading"
                    size="small"
                    @click="handleSubmit"
                >
                    {{ $t('apply') }}
                </BaseButton>
            </div>
        </BaseCard>

        <transition name="card">
            <BaseCard v-if="tokenLink" padded class="l-stack l-gap-1">
                <p>
                    {{ $t('confirmation') }}
                </p>

                <div class="l-inline l-center-v l-spread l-gap-2">
                    <a
                        :href="tokenLink"
                        class="t-truncated t-underline"
                        target="_blank"
                    >
                        {{ tokenLink }}
                    </a>

                    <div
                        v-if="isCopyActionSupported || isShareActionSupported"
                        class="l-inline l-gap-1"
                    >
                        <IconButton
                            v-if="isCopyActionSupported"
                            v-tooltip="{
                                content: $t('actionCopyDone'),
                                trigger: 'manual',
                                show: isTokenLinkCopied,
                            }"
                            :disabled="isTokenLinkCopied"
                            :title="$t('actionCopy')"
                            @click="handleTokenLinkCopy"
                        >
                            <CopyIcon />
                        </IconButton>

                        <IconButton
                            v-if="isShareActionSupported"
                            :title="$t('actionShare')"
                            @click="handleTokenLinkShare"
                        >
                            <ShareIcon />
                        </IconButton>
                    </div>
                </div>
            </BaseCard>
        </transition>
    </BaseModal>
</template>

<script>
import moment from 'moment'

import { httpHelper } from '@/utils'
import BaseButton from './BaseButton'
import BaseCard from './BaseCard'
import BaseInput from './BaseInput'
import BaseModal from './BaseModal'
import BaseMultiselect from './BaseMultiselect'
import CopyIcon from '../icons/CopyIcon'
import DateInput from './DateInput'
import IconButton from '../IconButton'
import ShareIcon from '../icons/ShareIcon'

export default {
    name: 'MeasurementsFilterModal',
    components: {
        BaseButton,
        BaseCard,
        BaseInput,
        BaseModal,
        BaseMultiselect,
        CopyIcon,
        DateInput,
        IconButton,
        ShareIcon,
    },
    props: {
        assetIds: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            description: '',
            isLoading: false,
            isTokenLinkCopied: false,
            time: null,
            timeCustom: null,
            timeOptions: ['custom', 1, 8, 24, 24 * 7, 'permanent'],
            token: null,
        }
    },
    computed: {
        isCopyActionSupported() {
            return !!window.navigator.clipboard
        },
        isDisabled() {
            return !this.time || (this.time === 'custom' && !this.timeCustom)
        },
        isShareActionSupported() {
            return !!window.navigator.share
        },
        timeCustomDisabled() {
            return {
                to: moment()
                    .subtract(1, 'day')
                    .endOf('day')
                    .toDate(),
            }
        },
        tokenLink() {
            return this.token
                ? `${window.location.origin}/#/share/${this.token}`
                : null
        },
    },
    watch: {
        description() {
            this.token = null
        },
        time() {
            this.token = null
        },
        timeCustom() {
            this.token = null
        },
    },
    methods: {
        handleClear() {
            this.description = ''
            this.time = null
            this.timeCustom = null
        },
        async handleSubmit() {
            this.isLoading = true
            const { data } = await httpHelper.post('/share/tokens/', {
                description: this.description,
                valid_from: moment().toJSON(),
                valid_to:
                    this.time === 'custom'
                        ? moment(this.timeCustom)
                              .endOf('day')
                              .toJSON()
                        : this.time === 'permanent'
                        ? moment()
                              .add(20, 'years')
                              .toJSON()
                        : moment()
                              .add(this.time, 'hours')
                              .toJSON(),
                assets: this.assetIds,
            })
            this.token = data.token
            this.isLoading = false
        },
        handleTokenLinkCopy() {
            window.navigator.clipboard.writeText(this.tokenLink)
            this.isTokenLinkCopied = true
            setTimeout(() => {
                this.isTokenLinkCopied = false
            }, 1000)
        },
        handleTokenLinkShare() {
            window.navigator.share({
                title: process.env.VUE_APP_TITLE,
                text: this.description,
                url: this.tokenLink,
            })
        },
    },
}
</script>

<i18n>
{
    "en": {
        "actionCopy": "Copy",
        "actionCopyDone": "Copied",
        "actionShare": "Share",
        "apply": "Generate link",
        "clear": "Clear",
        "confirmation": "Your sharing link is ready:",
        "custom": "Custom",
        "description": "This function allows to share the position of assets for a defined amount of time",
        "endDate": "End date",
        "hour": "{n} hour | {n} hours",
        "permanent": "Permanent",
        "placeholderDescription": "Description",
        "placeholderTime": "Time",
        "title": "Share",
        "week": "{n} week | {n} weeks"
    },
    "de": {
        "actionCopy": "Kopieren",
        "actionCopyDone": "Kopiert",
        "actionShare": "Teilen",
        "apply": "Link generieren",
        "clear": "Löschen",
        "confirmation": "Ihr Link zum Teilen ist bereit:",
        "custom": "Benutzerdefiniert",
        "description": "Diese Funktion ermöglicht es, die Position von Assets für eine bestimmte Zeitspanne zu teilen",
        "endDate": "Enddatum",
        "hour": "{n} Stunde | {n} Stunden",
        "permanent": "Permanent",
        "placeholderDescription": "Beschreibung",
        "placeholderTime": "Zeit",
        "title": "Teilen",
        "week": "{n} Woche | {n} Wochen"
    },
    "fr": {
        "actionCopy": "Copie",
        "actionCopyDone": "Copié",
        "actionShare": "Partager",
        "apply": "Générer un lien",
        "clear": "Effacer",
        "confirmation": "Votre lien de partage est prêt:",
        "custom": "Personnalisé",
        "description": "Cette fonction permet de partager la position des actifs pour une période de temps définie",
        "endDate": "Date de fin",
        "hour": "{n} heure | {n} heures",
        "permanent": "Permanent",
        "placeholderDescription": "Description",
        "placeholderTime": "Temps",
        "title": "Partager",
        "week": "{n} semaine | {n} semaines"
    },
    "it": {
        "actionCopy": "Copia",
        "actionCopyDone": "Copiato",
        "actionShare": "Condividi",
        "apply": "Generare un link",
        "clear": "Chiaro",
        "confirmation": "Il vostro link di condivisione è pronto:",
        "custom": "Personalizzato",
        "description": "Questa funzione consente di condividere la posizione degli asset per un periodo di tempo definito",
        "endDate": "Data di fine",
        "hour": "{n} ora | {n} ore",
        "permanent": "Permanente",
        "placeholderDescription": "Descrizione",
        "placeholderTime": "Tempo",
        "title": "Condividi",
        "week": "{n} settimana | {n} settimane"
    }
}
</i18n>

<style lang="scss">
.asset-sharing-modal {
    .card {
        &-enter,
        &-leave-to {
            opacity: 0;
            transform: scale(0.95);
        }
    }
}
</style>
